import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LoginService } from '@shared-angular/services/login.service';
import { BotidService } from '@shared-angular/services/ragai/botid.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { audioSaySchema, audioTextSchema } from '@shared/ragai.schemas';
import { firstValueFrom } from 'rxjs';
import { z } from 'zod';
import { AudioToTextService } from './audio-to-text/audio-to-text.service';
import { PostType } from './iframe-communication/botapi-manager.service';
import { KeyValService } from './key-val.service';
import { PlayAudioService } from './playaudio.service';

/**
 * Steuert Sprachausgabe, Mikrofonstatus und Sprachanweisungen (`RAGAI.AUDIO`).
 * @remarks
 * `voice` akzeptiert ausschließlich ElevenLabs Voice-IDs (aus dem jeweiligen ElevenLabs-Konto), keine anderen
 * TTS-Anbieter-Kennungen. Ohne Angabe wird die im Bot konfigurierte bzw. zuletzt über KeyVal gespeicherte
 * Standardstimme verwendet ({@link mp3BufferFromString} lädt sie bei Bedarf einmalig nach und cached sie).
 * {@link botSay} verhält sich je nach Bot-Modus unterschiedlich: Im Avatar-/LiveAvatar-Modus wird die Anfrage an
 * den Avatar-Stream weitergereicht (kein lokaler Audio-Player), sonst wird lokal ein `<audio>`-Element erzeugt
 * und abgespielt. {@link setInstruction} beeinflusst nur **wie** zukünftige Sprachausgaben klingen (Tonlage,
 * Tempo, Emotion als Freitext-Anweisung an die TTS), nicht was gesagt wird.
 */
@Injectable({
  providedIn: 'root'
})
export class AudioManagerService {
  constructor(
    private attService: AudioToTextService,
    private keyValService: KeyValService,
    private playAudio: PlayAudioService,
    private botidService: BotidService,
    private loginService: LoginService,
    private toastr: ToastrService,
    private http: HttpClient
  ) { }

  /**
   * Spielt einen Text per Text-to-Speech ab.
   * @param text Der vorzulesende Text oder ein Objekt gemäß `audioSaySchema`.
   * @param voice Optionale Stimme, die anstelle der konfigurierten Standardstimme verwendet wird.
   * @returns Nichts.
   */
  public async say(text: string | z.input<typeof audioSaySchema>, voice?: string): Promise<void> {
    const parsed = audioSaySchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      voice = parsed.data.voice ?? voice;
      text = parsed.data.text;
    }
    await this.playAudio.playAudio(text as string, this.botidService.getBotId(), voice);
  }

  /**
   * Überschreibt den angezeigten Text im Audio-Bot (`audiobot_overrideText`).
   * @param text Der anzuzeigende Text oder ein Objekt gemäß `audioTextSchema`.
   */
  public setText(text: string | z.input<typeof audioTextSchema>): void {
    const parsed = audioTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    window.audiobot_overrideText = text as string;
  }

  /**
   * Prüft, ob das Mikrofon aktuell stummgeschaltet ist.
   * @returns `true`, wenn das Mikrofon stummgeschaltet ist.
   */
  public isMicMuted(): boolean {
    return this.attService.isMuted;
  }

  /** Schaltet das Mikrofon ein (Stummschaltung aufheben). */
  public unmuteMic(): void {
    this.attService.setMuted(false);
  }

  /** Schaltet das Mikrofon stumm. */
  public muteMic(): void {
    this.attService.setMuted(true);
  }

  /**
   * Speichert eine Sprachanweisung, die bei der nächsten Sprachausgabe berücksichtigt wird.
   * @param text Die Anweisung oder ein Objekt gemäß `audioTextSchema`.
   * @returns Der gespeicherte KeyVal-Eintrag.
   */
  public setInstruction(text: string | z.input<typeof audioTextSchema>): Promise<unknown> {
    const parsed = audioTextSchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      text = parsed.data.text;
    }
    return this.keyValService.storeKeyValEntry("AUDIO_INSTRUCTION", text, "CONFIGURATION");
  }

  /**
   * Liest die zuvor gespeicherte Sprachanweisung aus.
   * @returns Die gespeicherte Anweisung, oder ein leerer String.
   */
  public async getInstruction() {
    const val = (await this.keyValService.getKeyValByType("CONFIGURATION")).find((a) => a.key == "AUDIO_INSTRUCTION")?.value;
    if (!val) {
      return "";
    }
    return val as string;
  }

  /** Stoppt die aktuell laufende Sprachausgabe. */
  public stopSpeaking() {
    this.playAudio.stopAudio();
  }

  /**
   * Spricht einen Text als Bot (per Avatar-Stream oder lokalem Audio-Player, je nach Modus).
   * @param text Der zu sprechende Text oder ein Objekt gemäß `audioSaySchema`.
   * @param voice Optionale Stimme, die anstelle der konfigurierten Standardstimme verwendet wird.
   * @returns Der gesprochene Text.
   */
  public async botSay(text: string | z.input<typeof audioSaySchema>, voice?: string): Promise<string | undefined> {
    const parsed = audioSaySchema.safeParse(text);
    if (parsed.success && typeof text === 'object') {
      voice = parsed.data.voice ?? voice;
      text = parsed.data.text;
    }
    if (window.location.pathname.includes("did-avatar")) {
      const botId = this.botidService.getBotId();
      firstValueFrom(
        this.http.post<{ ok: boolean }>("/api/toolbox/did/speak/" + botId, { text })
      ).catch(() => {
        this.toastr.error("ERROR", $localize`:@@did-avatar-connect-error:Die Verbindung zum Avatar ist fehlgeschlagen. Versuche es erneut.`);
      });
    } else if (window.location.pathname.includes("avatar")) {
      const botId = this.botidService.getBotId();
      firstValueFrom(
        this.http.post<{ ok: boolean }>("/api/toolbox/liveavatar/speak/" + botId, { text })
      ).catch(() => {
        this.toastr.error("ERROR", $localize`:@@liveavatar-connect-error:Die Verbindung zum Avatar ist fehlgeschlagen. Versuche es erneut.`);
      });
    } else {
      console.log("playing audio");
      const blobparts = await this.mp3BufferFromString(text as string, voice);
      document.getElementById('audioplayer')?.remove();
      const audio = document.createElement('audio');
      audio.id = 'audioplayer';
      audio.autoplay = true;
      const blob = new Blob([blobparts], { type: 'audio/mp3' });
      const blobUrl = URL.createObjectURL(blob);
      audio.src = blobUrl;
      audio.onended = () => {
        void window.RAGAI.BOTAPI.postMessage(PostType.botSayEnded, "");
        void window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.AUDIO.botSayEnded");
      };
      document.body.appendChild(audio);
    }
    return text as string;
  }

  /**
   * Erzeugt einen MP3-Audiopuffer aus Text per Text-to-Speech-API.
   * @param payload Der umzuwandelnde Text.
   * @param voice Optionale Stimme; wird sonst aus der Konfiguration geladen.
   * @returns Der MP3-Audiopuffer.
   */
  public async mp3BufferFromString(payload: string, voice: string | undefined) {
    const botId = this.botidService.getBotId();
    await window.RAGAI.BOTAPI.postMessage(PostType.botSayLoad, "");
    void window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.AUDIO.botSayLoad");
    voice = (await this.keyValService.getKeyValByType("CONFIGURATION")).find((a) => a.key == "AUDIO_VOICE")?.value as string || undefined;
    if (voice == undefined) {
      const res = await firstValueFrom(this.http.get<{ voice: string }>("/api/toolbox/audio/getvoice/" + botId));
      voice = res.voice;
      await this.keyValService.storeKeyValEntry("AUDIO_VOICE", voice, "CONFIGURATION");
    }
    const body = { input: payload, botId, userId: this.loginService.loggedInUser?._id, voice };
    const content = await firstValueFrom(this.http.post("/api/toolbox/audio/create", body, { responseType: 'arraybuffer' }));
    await window.RAGAI.BOTAPI.postMessage(PostType.botSayLoadComplete, content);
    void window.RAGAI.EVENTS._trigger_RAGAI_EVENT("RAGAI.AUDIO.botSayLoadComplete");
    return content;
  }
}
